import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Code2, Cpu, Server, Terminal, MonitorSmartphone, Paintbrush } from "lucide-react";
import {
  SiReact, SiJavascript, SiHtml5, SiTailwindcss, SiTypescript, 
  SiNextdotjs, SiRedux, SiNodedotjs, SiExpress, SiMongodb, SiPostman, 
  SiFirebase, SiGit, SiDocker, SiJenkins, SiJest, SiWebpack,
} from "react-icons/si";

// Fully populated categories array utilizing Lucide icons for UI and SimpleIcons for Brands
const SKILL_CATEGORIES = [
  {
    id: "frontend",
    category: "Frontend",
    icon: <MonitorSmartphone className="w-4 h-4" />,
    skills: [
      { name: "React", icon: <SiReact className="w-full h-full" />, color: "text-[#61DAFB]" },
      { name: "JavaScript", icon: <SiJavascript className="w-full h-full" />, color: "text-[#F7DF1E]" },
      { name: "HTML5", icon: <SiHtml5 className="w-full h-full" />, color: "text-[#E34F26]" },
      // Safe fallback for CSS using native Lucide icon to prevent compilation errors
      { name: "CSS3", icon: <Paintbrush className="w-full h-full" />, color: "text-[#1572B6]" },
      { name: "Tailwind", icon: <SiTailwindcss className="w-full h-full" />, color: "text-[#06B6D4]" },
      { name: "TypeScript", icon: <SiTypescript className="w-full h-full" />, color: "text-[#3178C6]" },
      { name: "Next.js", icon: <SiNextdotjs className="w-full h-full" />, color: "text-zinc-900 dark:text-white" },
      { name: "Redux", icon: <SiRedux className="w-full h-full" />, color: "text-[#764ABC]" },
    ],
  },
  {
    id: "backend",
    category: "Backend",
    icon: <Server className="w-4 h-4" />,
    skills: [
      { name: "Node.js", icon: <SiNodedotjs className="w-full h-full" />, color: "text-[#339933]" },
      { name: "Express", icon: <SiExpress className="w-full h-full" />, color: "text-zinc-900 dark:text-white" },
      { name: "MongoDB", icon: <SiMongodb className="w-full h-full" />, color: "text-[#47A248]" },
      { name: "REST APIs", icon: <SiPostman className="w-full h-full" />, color: "text-[#FF6C37]" },
      { name: "Firebase", icon: <SiFirebase className="w-full h-full" />, color: "text-[#FFCA28]" },
    ],
  },
  {
    id: "tools",
    category: "DevOps & Tools",
    icon: <Terminal className="w-4 h-4" />,
    skills: [
      { name: "Git", icon: <SiGit className="w-full h-full" />, color: "text-[#F05032]" },
      { name: "Docker", icon: <SiDocker className="w-full h-full" />, color: "text-[#2496ED]" },
      { name: "CI/CD", icon: <SiJenkins className="w-full h-full" />, color: "text-[#D24939]" },
      { name: "Jest", icon: <SiJest className="w-full h-full" />, color: "text-[#C21325]" },
      { name: "Webpack", icon: <SiWebpack className="w-full h-full" />, color: "text-[#8DD6F9]" },
    ],
  },
];

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const allSkills = SKILL_CATEGORIES.flatMap((c) =>
    c.skills.map((s) => ({ ...s, category: c.category, categoryId: c.id }))
  );
  
  const filteredSkills = activeCategory === "all"
    ? allSkills
    : allSkills.filter((s) => s.categoryId === activeCategory);

  return (
    <section id="skills" className="py-24 bg-[#FAFAFA] dark:bg-[#09090B] px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 mb-6">
            <Code2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-zinc-900 dark:text-white mb-6">
            Technical <span className="text-zinc-400 dark:text-zinc-500">Arsenal</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-zinc-600 dark:text-zinc-400 leading-relaxed mb-10">
            The frameworks, languages, and tools I use to architect scalable web applications and resilient backend services.
          </p>

          <div className="flex flex-wrap justify-center gap-2 p-1.5 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 rounded-full max-w-fit mx-auto">
            <button
              onClick={() => setActiveCategory("all")}
              className={`relative flex items-center px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                activeCategory === "all"
                  ? "text-white dark:text-zinc-900"
                  : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
              }`}
            >
              {activeCategory === "all" && (
                <motion.div
                  layoutId="active-skill-pill"
                  className="absolute inset-0 bg-zinc-900 dark:bg-zinc-100 rounded-full"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-2">
                <Cpu className="w-4 h-4" /> All
              </span>
            </button>
            
            {SKILL_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCategory(c.id)}
                className={`relative flex items-center px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                  activeCategory === c.id
                    ? "text-white dark:text-zinc-900"
                    : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                }`}
              >
                {activeCategory === c.id && (
                  <motion.div
                    layoutId="active-skill-pill"
                    className="absolute inset-0 bg-zinc-900 dark:bg-zinc-100 rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  {c.icon} {c.category}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          layout
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6"
        >
          <AnimatePresence mode="popLayout">
            {filteredSkills.map((skill) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                key={skill.name}
                className="group relative flex flex-col items-center p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 hover:border-emerald-500/50 dark:hover:border-emerald-500/50 hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300"
              >
                <div className={`w-12 h-12 mb-4 transition-transform duration-300 group-hover:scale-110 ${skill.color} drop-shadow-sm`}>
                  {skill.icon}
                </div>
                <h3 className="text-center text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                  {skill.name}
                </h3>
                <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-500 dark:bg-white/5 dark:text-zinc-400">
                  {skill.category}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;